<script>
  import { range, format } from "d3";
  export let min = 0;
  export let max = 1;
  export let step = 0.002;
  export let value = max;
  export let override = false;
  export let ticks = [];
  export let width = 0;
  // export let orientation = "horizontal";
  export let shouldLabel = true;
  export let shouldTick = false;
  export let label = "";

  const THUMB_RAD = 18;

  // const ticksReverse = ticks.map(({ name, offset }) => ({ name, offset }));
  // console.log(ticksReverse);
  const getLabel = () => {
    const match = ticks.find((t) => value <= 1 - t.offset);
    return match ? match.name : ticks[0].name;
  };

  $: index = step < 1 ? Math.round(value * (ticks.length - 1)) : value;
  $: thumbPos = (value / max) * (width - THUMB_RAD * 2) + THUMB_RAD;
  $: label = getLabel(value);
  $: if (override) value = override;
</script>

<div class="range" style="width: {width}px;">
  {#if ticks}
    <div class="ticks">
      {#each ticks as { name, offset }, i}
        <div class="tick" style="right: {offset * 100}%">
          <span class="tick--mark"></span>
          <span class="tick--label" class:is-visible="{shouldTick}">{name}</span
          >
        </div>
      {/each}
    </div>
  {/if}
  {#if shouldLabel}
    <p class="label" style="left: {thumbPos}px;">{label}</p>
  {/if}
  <input type="range" min="{min}" max="{max}" step="{step}" bind:value />
</div>

<style>
  .range {
    --thumb-width: 36px;
    --tick-font-size: 14px;
    width: 100%;
    pointer-events: auto;
    position: relative;
    outline-color: transparent;
  }

  input[type="range"] {
    display: block;
    width: 100%;
    height: var(--thumb-width);
    appearance: none;
    cursor: pointer;
    padding: 0;
    margin: 0;
    background: transparent;
    position: relative;
    outline: none;
  }

  input[type="range"]:focus {
    box-shadow: 0 0 0 0 transparent;
  }

  input[type="range"]:focus::-webkit-slider-thumb,
  input[type="range"]:focus::-moz-range-thumb,
  input[type="range"]:focus::-ms-thumb {
    box-shadow: 0 0 0 0 transparent;
  }

  input[type="range"]::-webkit-slider-thumb {
    height: var(--thumb-width);
    width: var(--thumb-width);
    border-radius: 50%;
    appearance: none;
    margin-top: calc(var(--thumb-width) / -2);
    background: repeating-radial-gradient(
      circle,
      var(--base-white),
      var(--base-white) 3px,
      var(--base-black) 3px,
      var(--base-black) 10px,
      var(--theme) 10px,
      var(--theme) 11px,
      var(--base-black) 11px,
      var(--base-black) 24px
    );
    border: 1px dashed var(--base-white);
    transform: translate(0, calc(var(--thumb-width) * 0.5));
  }

  input[type="range"]::-moz-range-thumb {
    box-shadow: 1px 1px 1px #000, 0 0 1px #0d0d0d;
    height: var(--thumb-width);
    width: var(--thumb-width);
    border-radius: 50%;
    background: repeating-radial-gradient(
      circle,
      var(--base-white),
      var(--base-white) 3px,
      var(--base-black) 3px,
      var(--base-black) 10px,
      var(--theme) 10px,
      var(--theme) 11px,
      var(--base-black) 11px,
      var(--base-black) 24px
    );
    border: 1px dashed var(--base-white);
    /* transform: translate(0, calc(var(--thumb-width) * 0.5)); */
  }

  input[type="range"]::-ms-thumb {
    height: var(--thumb-width);
    width: var(--thumb-width);
    border-radius: 50%;
    background: repeating-radial-gradient(
      circle,
      var(--base-white),
      var(--base-white) 3px,
      var(--base-black) 3px,
      var(--base-black) 10px,
      var(--theme) 10px,
      var(--theme) 11px,
      var(--base-black) 11px,
      var(--base-black) 24px
    );
  }

  input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: var(--thumb-width);
    background: transparent;
  }

  input[type="range"]::-moz-range-track {
    width: 100%;
    height: var(--thumb-width);
    background: transparent;
  }

  input[type="range"]:focus::-webkit-slider-runnable-track {
    background: transparent;
  }

  input[type="range"]::-ms-track {
    width: 100%;
    height: 1px;
    background: transparent;
    border-color: transparent;
    border-width: 16px 0;
    color: transparent;
  }

  input[type="range"]::-ms-fill-lower,
  input[type="range"]::-ms-fill-upper {
    background: var(--base-gray-medium);
    border: 0.2px solid #010101;
    border-radius: 4px;
    box-shadow: 1px 1px 1px #000, 0 0 1px #0d0d0d;
  }

  input[type="range"]:focus::-ms-fill-lower,
  input[type="range"]:focus::-ms-fill-upper {
    background: var(--base-gray-medium);
  }

  .ticks {
    width: 100%;
    padding: 0;
    margin: 0;
    user-select: none;
    position: absolute;
    left: 0;
    bottom: 0;
    transform: translate(0, calc(var(--thumb-width) * -0.5));
    border-top: 1px solid var(--base-gray-medium);
  }

  .tick {
    margin: 0;
    font-size: var(--tick-font-size);
    width: 1px;
    background: transparent;
    color: var(--base-gray);
    position: absolute;
  }

  .tick:first-of-type {
    margin-left: calc(var(--thumb-width) * 0.5);
  }

  .tick:last-of-type {
    margin-right: calc(var(--thumb-width) * 0.5);
  }

  .tick--mark {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: calc(var(--thumb-width) * 0.5);
    background: var(--base-gray-medium);
    transform: translate(0, -50%);
  }

  .tick--label {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    font-weight: bold;
    text-align: center;
    transform: translate(-50%, -175%);
    opacity: 0;
    transition: opacity 0.25s;
    width: 6em;
  }

  .tick span.is-visible {
    opacity: 1;
  }

  .tick:first-of-type {
    transform: translate(-1px, 0);
  }

  .tick:last-of-type {
    transform: translate(1px, 0);
  }

  .label {
    position: absolute;
    top: calc(var(--thumb-width) * 0.5);
    left: 0;
    margin: 0;
    font-weight: bold;
    font-size: var(--tick-font-size);
    transform: translate(-50%, -225%);
    text-transform: uppercase;
    white-space: nowrap;
    color: var(--theme);
  }
</style>
